import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Avatar } from '@/components/ui/Avatar'
import {
  addGroupMember,
  fetchGroupSummary,
  removeGroupMember,
  updateGroupMemberRole,
} from '@/api/groups'
import { fetchFriends } from '@/api/users'
import { useGroupPermissions } from '@/hooks/useGroupPermissions'
import { toFriend } from '@/lib/steam'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import type { GroupMember, GroupRole, GroupSummary } from '@/types'

interface MembersModalProps {
  groupId: string
  onClose: () => void
}

const ROLE_LABELS: Record<GroupRole, string> = {
  admin: 'Admin',
  user: 'User',
  voter: 'Voter',
}

function roleBadge(member: GroupMember): string {
  if (member.isCreator) return 'Creator'
  return ROLE_LABELS[member.role]
}

export function MembersModal({ groupId, onClose }: MembersModalProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const { setActiveGroupId } = useUIStore()
  const { canManageGroup } = useGroupPermissions()

  const [search, setSearch] = useState('')

  const summaryKey = ['group-summary', groupId]
  const { data: summary } = useQuery({
    queryKey: summaryKey,
    queryFn: () => fetchGroupSummary(groupId),
  })
  const members = summary?.members ?? []
  const memberIds = new Set(members.map((m) => m.steamId))

  const { data: friendsData } = useQuery({
    queryKey: ['friends'],
    queryFn: fetchFriends,
    enabled: canManageGroup,
  })

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  // Shared optimistic patch helper over the cached summary's member list.
  function patchMembers(fn: (m: GroupMember[]) => GroupMember[]) {
    queryClient.setQueryData<GroupSummary>(summaryKey, (old) =>
      old ? { ...old, members: fn(old.members) } : old,
    )
  }

  const roleMutation = useMutation({
    mutationFn: ({ steamId, role }: { steamId: string; role: GroupRole }) =>
      updateGroupMemberRole(groupId, steamId, role),
    onMutate: async ({ steamId, role }) => {
      await queryClient.cancelQueries({ queryKey: summaryKey })
      const previous = queryClient.getQueryData<GroupSummary>(summaryKey)
      patchMembers((ms) => ms.map((m) => (m.steamId === steamId ? { ...m, role } : m)))
      return { previous }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(summaryKey, ctx.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: summaryKey }),
  })

  const removeMutation = useMutation({
    mutationFn: (steamId: string) => removeGroupMember(groupId, steamId),
    onMutate: async (steamId) => {
      await queryClient.cancelQueries({ queryKey: summaryKey })
      const previous = queryClient.getQueryData<GroupSummary>(summaryKey)
      patchMembers((ms) => ms.filter((m) => m.steamId !== steamId))
      return { previous }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(summaryKey, ctx.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: summaryKey })
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
  })

  const addMutation = useMutation({
    mutationFn: (steamId: string) => addGroupMember(groupId, steamId),
    onMutate: async (steamId) => {
      await queryClient.cancelQueries({ queryKey: summaryKey })
      const previous = queryClient.getQueryData<GroupSummary>(summaryKey)
      const friend = (friendsData?.friends ?? []).find((f) => f.steamId === steamId)
      if (friend) {
        patchMembers((ms) => [...ms, { ...friend, role: 'user', isCreator: false }])
      }
      return { previous }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(summaryKey, ctx.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: summaryKey })
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
  })

  const leaveMutation = useMutation({
    mutationFn: () => removeGroupMember(groupId, user!.steamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      setActiveGroupId(null)
      onClose()
      navigate('/wishlist')
    },
  })

  const currentIsCreator = members.find((m) => m.steamId === user?.steamId)?.isCreator ?? false

  const addableFriends = (friendsData?.friends ?? [])
    .filter((f) => !memberIds.has(f.steamId))
    .filter((f) => {
      const q = search.toLowerCase()
      if (!q) return true
      return (
        (f.nickname ?? f.name).toLowerCase().includes(q) ||
        f.name.toLowerCase().includes(q) ||
        f.handle.toLowerCase().includes(q)
      )
    })

  return createPortal(
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cg-modal-header">
          <h2>{canManageGroup ? 'Manage members' : 'Members'}</h2>
          <button className="cg-icon-btn cg-modal-close-x" onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ padding: '8px 10px 16px' }}>
            {members.map((m) => {
              const friend = toFriend(m)
              const isSelf = m.steamId === user?.steamId
              return (
                <div key={m.steamId} className="cg-pick-item" style={{ cursor: 'default' }}>
                  <Avatar friend={friend} size={28} />
                  <div className="cg-pick-body">
                    <div className="cg-pick-name">
                      {m.nickname ?? m.name}
                      {isSelf && (
                        <span style={{ color: 'var(--fg-3)', fontWeight: 400 }}> (you)</span>
                      )}
                    </div>
                    <div className="cg-pick-status">@{m.handle}</div>
                  </div>
                  {canManageGroup && !m.isCreator ? (
                    <select
                      className="cg-input"
                      style={{ width: 'auto', padding: '2px 6px' }}
                      value={m.role}
                      disabled={roleMutation.isPending}
                      onChange={(e) =>
                        roleMutation.mutate({
                          steamId: m.steamId,
                          role: e.target.value as GroupRole,
                        })
                      }
                    >
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                      <option value="voter">Voter</option>
                    </select>
                  ) : (
                    <span
                      className="cg-pick-status"
                      style={{
                        border: '1px solid var(--line)',
                        borderRadius: 'var(--radius)',
                        padding: '2px 8px',
                        color: m.isCreator ? 'var(--accent)' : 'var(--fg-2)',
                      }}
                    >
                      {roleBadge(m)}
                    </span>
                  )}
                  {canManageGroup && !m.isCreator && !isSelf && (
                    <button
                      type="button"
                      className="cg-icon-btn"
                      title="Remove from group"
                      style={{ marginLeft: 6 }}
                      disabled={
                        removeMutation.isPending && removeMutation.variables === m.steamId
                      }
                      onClick={() => removeMutation.mutate(m.steamId)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          {canManageGroup && (
            <>
              <div className="cg-modal-section">Add member</div>
              <div style={{ padding: '0 10px' }}>
                <input
                  className="cg-input"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search friends…"
                />
              </div>
              <div style={{ padding: '8px 10px 16px' }}>
                {addableFriends.map((f) => {
                  const friend = toFriend(f)
                  const pending =
                    addMutation.isPending && addMutation.variables === f.steamId
                  return (
                    <div
                      key={f.steamId}
                      className="cg-pick-item"
                      onClick={() => !pending && addMutation.mutate(f.steamId)}
                    >
                      <Avatar friend={friend} size={28} />
                      <div className="cg-pick-body">
                        <div className="cg-pick-name">{f.nickname ?? f.name}</div>
                        <div className="cg-pick-status">@{f.handle}</div>
                      </div>
                      <div className="cg-pick-check">{pending ? '…' : '+'}</div>
                    </div>
                  )
                })}
                {addableFriends.length === 0 && (
                  <div
                    style={{
                      padding: '16px 0',
                      textAlign: 'center',
                      color: 'var(--fg-3)',
                      fontSize: 12,
                    }}
                  >
                    No friends to add
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="cg-modal-footer">
          {!currentIsCreator && user && (
            <button
              type="button"
              className="cg-btn danger"
              disabled={leaveMutation.isPending}
              onClick={() => leaveMutation.mutate()}
            >
              {leaveMutation.isPending ? 'Leaving…' : 'Leave group'}
            </button>
          )}
          <button className="cg-btn ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
